import React, { useState } from "react";
import {
  FIELD_TYPES,
  TYPES_OF_MESSAGES,
  TYPES_OF_SENDER,
} from "../../pages/constants/AiAgent";
import Button from "./Button";
import sia from "../../images/sia_face.png";
import { formatMessage, stripEmojis } from "../../utils/aiagent/aiAgentUtils";
import Tag from "./Tag";
import { IoIosCheckmarkCircle, IoIosCloseCircle } from "react-icons/io";
import {
  useHandleCloseSocket,
  useHandleMessageGlobally,
} from "../drawer/aiAgent/AiAgent";
import { useNavigate } from "react-router-dom";
import { REDIRECTION_LINKS } from "../../constants/common";
import PDFIMAGE from "../../images/pdfImg.png";
import { useSelector } from "react-redux";
import { getUserName } from "../../features/userid";
import ComponentRenderer from "../drawer/aiAgent/components/ComponentRenderer";
import SourcingProgress from "../drawer/aiAgent/components/SourcingProgress";

const FormLabel = ({ htmlFor, children }) => (
  <label htmlFor={htmlFor} className="mb-1 text-xs font-medium text-gray-500">
    {children}
  </label>
);

const FormInput = ({ label, name, value, onChange, disabled, placeholder }) => (
  <div className="flex flex-col">
    <FormLabel htmlFor={name}>{label}</FormLabel>
    <input
      id={name}
      name={name}
      value={value}
      onChange={onChange}
      disabled={disabled}
      placeholder={placeholder}
      className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg bg-gray-50 focus:outline-none focus:border-blue-400 disabled:opacity-50"
    />
  </div>
);

const FormSelect = ({ label, name, value, onChange, options, disabled }) => (
  <div className="flex flex-col">
    <FormLabel htmlFor={name}>{label}</FormLabel>
    <select
      id={name}
      name={name}
      value={value}
      onChange={onChange}
      disabled={disabled}
      className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg bg-gray-50 focus:outline-none focus:border-blue-400 disabled:opacity-50 appearance-none"
    >
      {options.map((opt) => (
        <option key={opt.value} value={opt.value}>
          {opt.label}
        </option>
      ))}
    </select>
  </div>
);

const FormTextarea = ({ label, name, value, onChange, disabled, placeholder }) => (
  <div className="flex flex-col">
    <FormLabel htmlFor={name}>{label}</FormLabel>
    <textarea
      id={name}
      name={name}
      rows="3"
      value={value}
      onChange={onChange}
      disabled={disabled}
      placeholder={placeholder}
      className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg bg-gray-50 focus:outline-none focus:border-blue-400 disabled:opacity-50 resize-none"
    />
  </div>
);

const FormTagInput = ({ label, name, value, onChange, disabled, placeholder }) => {
  const [tags, setTags] = useState(value ? value.split(",").filter(Boolean).map(t => t.trim()) : []);
  const [inputValue, setInputValue] = useState("");

  const handleKeyDown = (e) => {
    if (e.key === "," || e.key === "Enter") {
      e.preventDefault();
      const val = inputValue.trim().replace(/,$/, "");
      if (val && !tags.includes(val)) {
        const newTags = [...tags, val];
        setTags(newTags);
        onChange(newTags.join(", "));
      }
      setInputValue("");
    } else if (e.key === "Backspace" && !inputValue && tags.length) {
      const newTags = tags.slice(0, -1);
      setTags(newTags);
      onChange(newTags.join(", "));
    }
  };

  const removeTag = (index) => {
    const newTags = tags.filter((_, i) => i !== index);
    setTags(newTags);
    onChange(newTags.join(", "));
  };

  return (
    <div className="flex flex-col">
      <FormLabel htmlFor={name}>{label}</FormLabel>
      <div className={`flex flex-wrap items-center gap-1.5 border border-gray-200 rounded-lg p-2 bg-gray-50 focus-within:border-blue-400 ${disabled ? "opacity-50 pointer-events-none" : ""}`}>
        {tags.map((tag, index) => (
          <span
            key={index}
            className="inline-flex items-center gap-1 bg-blue-50 text-blue-700 text-xs font-medium px-2 py-0.5 rounded-full"
          >
            {tag}
            {!disabled && (
              <button
                type="button"
                className="text-blue-500 hover:text-blue-800"
                onClick={() => removeTag(index)}
              >
                ×
              </button>
            )}
          </span>
        ))}
        <input
          type="text"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={tags.length === 0 ? placeholder : ""}
          disabled={disabled}
          className="flex-1 outline-none border-none bg-transparent text-sm min-w-[100px]"
        />
      </div>
    </div>
  );
};

const TypesOfBlockFromFieldName = ({
  fieldName,
  formData,
  setFormData,
  disabled,
}) => {
  switch (fieldName) {
    case FIELD_TYPES.JOBTITLE:
      return (
        <FormInput
          label="Job Title"
          name={FIELD_TYPES.JOBTITLE}
          value={formData[FIELD_TYPES.JOBTITLE]}
          onChange={(e) =>
            setFormData({ ...formData, [FIELD_TYPES.JOBTITLE]: e.target.value })
          }
          disabled={disabled}
        />
      );
    case FIELD_TYPES.LOCATION:
      return (
        <FormInput
          label="Location"
          name={FIELD_TYPES.LOCATION}
          value={formData[FIELD_TYPES.LOCATION]}
          onChange={(e) =>
            setFormData({ ...formData, [FIELD_TYPES.LOCATION]: e.target.value })
          }
          disabled={disabled}
        />
      );
    case FIELD_TYPES.RELOCATION:
      return (
        <FormSelect
          label="Relocation"
          name={FIELD_TYPES.RELOCATION}
          value={formData[FIELD_TYPES.RELOCATION]}
          onChange={(e) =>
            setFormData({
              ...formData,
              [FIELD_TYPES.RELOCATION]: e.target.value,
            })
          }
          options={[
            { label: "--", value: "Not mentioned" },
            { label: "Yes", value: "yes" },
            { label: "No", value: "no" },
          ]}
          disabled={disabled}
        />
      );
    case FIELD_TYPES.MINEXPERIENCE:
      return (
        <div className="grid grid-cols-2 gap-3">
          <FormSelect
            label="Min Experience"
            name={FIELD_TYPES.MINEXPERIENCE}
            value={Number(formData[FIELD_TYPES.MINEXPERIENCE])}
            onChange={(e) => {
              const newMin = Number(e.target.value);
              const currentMax =
                Number(formData[FIELD_TYPES.MAXEXPERIENCE]) || 0;
              setFormData((prev) => ({
                ...prev,
                [FIELD_TYPES.MINEXPERIENCE]: newMin,
                [FIELD_TYPES.MAXEXPERIENCE]:
                  newMin > currentMax ? newMin : currentMax,
              }));
            }}
            options={Array.from({ length: 41 }, (_, i) => ({
              label: i,
              value: i,
            }))}
            disabled={disabled}
          />
          <FormSelect
            label="Max Experience"
            name={FIELD_TYPES.MAXEXPERIENCE}
            value={Number(formData[FIELD_TYPES.MAXEXPERIENCE])}
            onChange={(e) =>
              setFormData((prev) => ({
                ...prev,
                [FIELD_TYPES.MAXEXPERIENCE]: Number(e.target.value),
              }))
            }
            options={Array.from(
              { length: 40 - Number(formData[FIELD_TYPES.MINEXPERIENCE]) },
              (_, i) => ({
                label: i + Number(formData[FIELD_TYPES.MINEXPERIENCE]),
                value: i + Number(formData[FIELD_TYPES.MINEXPERIENCE]),
              })
            )}
            disabled={disabled}
          />
        </div>
      );
    case FIELD_TYPES.NICETOHAVE:
      return (
        <FormTagInput
          label="Nice to have skills"
          name={FIELD_TYPES.NICETOHAVE}
          value={formData[FIELD_TYPES.NICETOHAVE]}
          onChange={(e) =>
            setFormData({ ...formData, [FIELD_TYPES.NICETOHAVE]: e })
          }
          placeholder="Separate by comma"
          disabled={disabled}
        />
      );

    case FIELD_TYPES.SKILLS:
      return (
        <FormTagInput
          label="Must have skills"
          name={FIELD_TYPES.SKILLS}
          value={formData[FIELD_TYPES.SKILLS]}
          onChange={(e) =>
            setFormData({ ...formData, [FIELD_TYPES.SKILLS]: e })
          }
          placeholder="Separate by comma"
          disabled={disabled}
        />
      );

    default:
      return null;
  }
};

const ProfileAvatar = ({ isAI, name }) => {
  if (isAI) {
    return (
      <div className="flex-shrink-0 w-8 h-8 rounded-full bg-indigo-500 flex items-center justify-center text-white text-xs font-bold shadow-md">
        <img src={sia} alt="AI Avatar" />
      </div>
    );
  } else {
    return name && name.length > 0 ? (
      <div className="flex-shrink-0 w-8 h-8 rounded-full bg-indigo-500 flex items-center justify-center text-white text-xs font-bold shadow-md">
        {name.charAt(0).toUpperCase()}
      </div>
    ) : (
      <img
        src="https://placehold.co/32x32/5b21b6/ffffff?text=U"
        alt="User Avatar"
        className="flex-shrink-0 w-8 h-8 rounded-full border-2 border-indigo-500 shadow-md"
        onError={(e) => {
          e.target.onerror = null;
          e.target.src = "https://placehold.co/32x32/5b21b6/ffffff?text=U";
        }}
      />
    );
  }
};

const SubmitFormButton = ({ formData }) => {
  const finalData = Object.keys(formData).map((key) => {
    return {
      fieldName: key,
      value: formData[key],
    };
  });

  const { sendMessage } = useHandleMessageGlobally(finalData);

  const isDisabled = Object.keys(formData).some(
    (key) => key !== FIELD_TYPES.NICETOHAVE && formData[key] === ""
  );

  const [isDisabled2, setIsDisabled2] = useState(false);

  return (
    <Button
      variant="black"
      className="mt-6 !px-6"
      onClick={() => {
        sendMessage();
        setIsDisabled2(true);
      }}
      disabled={isDisabled || isDisabled2}
    >
      Continue
    </Button>
  );
};

const RelocationQuestionButtons = ({ options, questionType, isLast }) => {
  const [isDisabled, setIsDisabled] = useState(!isLast);

  const handleClick = (value) => {
    const message = {
      type: "relocation_answer",
      payload: {
        question_type: questionType || "relocation",
        value: value,
      },
    };

    if (window.activeChatSocket?.readyState === WebSocket.OPEN) {
      window.activeChatSocket.send(JSON.stringify(message));
      setIsDisabled(true);
    }
  };

  return (
    <div className="flex gap-3">
      {options?.map((option) => (
        <Button
          key={option.value}
          onClick={() => handleClick(option.value)}
          disabled={isDisabled}
          variant="secondary"
          className="!rounded-full px-6"
        >
          {option.label}
        </Button>
      ))}
    </div>
  );
};

const MessageBlockAI = ({ body, type, showFormButton, isLast, sourcingProgress, isCancelling, onCancelSourcing }) => {
  const jid = useSelector((state) => state.drawer.jid);
  const [formData, setFormData] = useState(
    type === TYPES_OF_MESSAGES.FORM
      ? body.payload.fields.reduce((acc, field) => {
          acc[field.fieldname] = field.value;
          return acc;
        }, {})
      : {}
  );

  const navigate = useNavigate();
  const { handleClose } = useHandleCloseSocket();

  switch (type) {
    case TYPES_OF_MESSAGES.CHAT:
      return (
        <div className="flex items-start justify-start space-x-3">
          <ProfileAvatar isAI={true} />
          <div className="bg-white p-3 rounded-md shadow-lg shadow-[#B9E3FF80] max-w-full border border-aiBorder">
            <p className="text-sm text-gray-700 whitespace-pre-wrap">
              {formatMessage(stripEmojis(body.payload.content))}
            </p>
          </div>
        </div>
      );
    case TYPES_OF_MESSAGES.FORM:
      return (
        <div className="flex items-start justify-start space-x-3">
          <ProfileAvatar isAI={true} />
          <div className="bg-white p-3 rounded-md shadow-lg shadow-[#B9E3FF80] min-w-[90%] border border-aiBorder">
            <p className="text-sm text-gray-700">Got it!</p>
            <p className="text-sm text-gray-700">
              Do you want to change any of the required input?
            </p>
            <form className="space-y-4 mt-4 w-full">
              {body.payload.fields?.map((field) => {
                return (
                  <TypesOfBlockFromFieldName
                    fieldName={field.fieldname}
                    formData={formData}
                    setFormData={setFormData}
                    disabled={!showFormButton}
                  />
                );
              })}
            </form>

            {showFormButton && <SubmitFormButton formData={formData} />}
          </div>
        </div>
      );
    case TYPES_OF_MESSAGES.OUTREACH: {
      const outreachStatus = body.payload.status || "completed";
      return (
        <div className="flex items-start justify-start space-x-3">
          <ProfileAvatar isAI={true} />
          <div className="bg-white p-3 rounded-md shadow-lg shadow-[#B9E3FF80] min-w-[90%] border border-aiBorder text-sm">
            <p className="text-gray-700 whitespace-pre-wrap mb-2">
              {body.payload.content}
            </p>
            {outreachStatus === "in_progress" && sourcingProgress && (
              <SourcingProgress
                progress={sourcingProgress}
                completed={sourcingProgress.completed}
                cancelled={sourcingProgress.cancelled}
                isCancelling={isCancelling}
                onCancel={onCancelSourcing}
              />
            )}
            {outreachStatus === "in_progress" && !sourcingProgress && (
              <div className="flex items-center gap-2 py-1 text-[13px] text-gray-500">
                <svg className="animate-spin" width="14" height="14" viewBox="0 0 14 14" fill="none">
                  <circle cx="7" cy="7" r="5.5" stroke="#E5E7EB" strokeWidth="2" />
                  <path d="M12.5 7a5.5 5.5 0 00-5.5-5.5" stroke="#148EFF" strokeWidth="2" strokeLinecap="round" />
                </svg>
                <span>Sourcing in progress...</span>
              </div>
            )}
            {outreachStatus === "completed" && (
              <div className="flex items-center gap-2 py-1 text-[13px] text-green-600">
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                  <circle cx="7" cy="7" r="7" fill="#16a34a" />
                  <path d="M4 7.2L6.1 9.3L10 5" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                <span>Sourcing completed</span>
              </div>
            )}
            {outreachStatus === "cancelled" && (
              <div className="flex items-center gap-2 py-1 text-[13px] text-gray-500">
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                  <circle cx="7" cy="7" r="7" fill="#9ca3af" />
                  <path d="M5 5L9 9M9 5L5 9" stroke="white" strokeWidth="1.5" strokeLinecap="round" />
                </svg>
                <span>Sourcing cancelled</span>
              </div>
            )}
            {outreachStatus === "error" && (
              <div className="flex items-center gap-2 py-1 text-[13px] text-red-600">
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                  <circle cx="7" cy="7" r="7" fill="#dc2626" />
                  <path d="M7 4v3.5M7 9.5h.01" stroke="white" strokeWidth="1.5" strokeLinecap="round" />
                </svg>
                <span>Sourcing failed</span>
              </div>
            )}
            {outreachStatus === "zero_results" && (
              <div className="flex items-center gap-2 py-1 text-[13px] text-amber-600">
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                  <circle cx="7" cy="7" r="7" fill="#f59e0b" />
                  <path d="M7 4v3.5M7 9.5h.01" stroke="white" strokeWidth="1.5" strokeLinecap="round" />
                </svg>
                <span>No candidates found</span>
              </div>
            )}
          </div>
        </div>
      );
    }
    case TYPES_OF_MESSAGES.SOURCING_ZERO_RESULTS:
      return (
        <div className="flex items-start justify-start space-x-3">
          <ProfileAvatar isAI={true} />
          <div className="bg-white p-3 rounded-md shadow-lg shadow-[#B9E3FF80] max-w-full border border-aiBorder">
            <p className="text-sm text-gray-700 whitespace-pre-wrap">
              {formatMessage(stripEmojis(body.payload.content))}
            </p>
          </div>
        </div>
      );
    case TYPES_OF_MESSAGES.SOURCING_DONE:
      return (
        <div className="flex items-start justify-start space-x-3">
          <ProfileAvatar isAI={true} />
          <div className="bg-white p-3 rounded-md shadow-lg shadow-[#B9E3FF80] min-w-[90%] border border-aiBorder text-sm">
            ✨ {body.payload.content} ✨
            <div className="relative z-30 w-fit mt-6">
              <div className="absolute z-50 -top-3 -right-3 w-fit">
                <Tag text={"New"} variant={"gradientBlue"} />
              </div>
              <Button
                variant="black"
                onClick={() => {
                  navigate(REDIRECTION_LINKS.outreachedJob, {
                    state: { id: body.payload.job_id || jid },
                  });
                  handleClose();
                }}
              >
                {" "}
                Checkout Job{" "}
              </Button>
            </div>
          </div>
           
        </div>
      );
    case TYPES_OF_MESSAGES.RELOCATION_QUESTION:
      const { question, options } = body.payload.content ? JSON.parse(body.payload.content) : body.payload;
      return (
        <div className="flex items-start justify-start space-x-3">
          <ProfileAvatar isAI={true} />
          <div className="bg-white p-3 rounded-md shadow-lg shadow-[#B9E3FF80] min-w-[90%] border border-aiBorder">
            <p className="text-sm text-gray-700 mb-4">
              {question}
            </p>
            <RelocationQuestionButtons
              options={options}
              questionType={body.payload.type}
              isLast={isLast}
            />
          </div>
        </div>
      );
    case TYPES_OF_MESSAGES.SOURCING_ERROR:
      return (
        <div className="flex items-start justify-start space-x-3">
          <ProfileAvatar isAI={true} />
          <div className="bg-white p-3 rounded-md shadow-lg shadow-[#B9E3FF80] max-w-full border border-aiBorder">
            <p className="text-sm text-gray-700 whitespace-pre-wrap">
              {formatMessage(stripEmojis(body.payload.content))}
            </p>
          </div>
        </div>
      );
    case TYPES_OF_MESSAGES.COMPONENT:
      return (
        <div className="flex items-start justify-start space-x-3">
          <ProfileAvatar isAI={true} />
          <div className="bg-white p-3 rounded-md shadow-lg shadow-[#B9E3FF80] min-w-[90%] border border-aiBorder">
            <ComponentRenderer payload={body.payload} disabled={!isLast} />
          </div>
        </div>
      );
    default:
      return null;
  }
};

const MessageBlockUser = ({ body, type, name }) => {
  return (
    <div className="flex items-end space-x-3 justify-end">
      <div className="max-w-[80%] text-black">
        <p className="text-sm font-medium w-full whitespace-pre-line">
          {body.payload.content}
        </p>
        {type === TYPES_OF_MESSAGES.RESUME && (
          <p className="text-xs text-black mt-2 bg-white p-2 rounded border border-mutedGray flex gap-2 items-center">
            <img src={PDFIMAGE} className="h-5 w-5" alt="PDF" />
            {body.payload.filename}
          </p>
        )}
      </div>
      <ProfileAvatar isAI={false} name={name} />
    </div>
  );
};

const MessageBlocks = ({ isAI, body, type, showFormButton = false, isLast, sourcingProgress, isCancelling, onCancelSourcing }) => {
  const userName = useSelector(getUserName);
  if (type === TYPES_OF_SENDER.ERROR)
    return <Tag variant={"danger"} text={body.payload.message} />;
  switch (body.payload.role) {
    case TYPES_OF_SENDER.AI:
    case TYPES_OF_SENDER.USER:
      return isAI ? (
        <MessageBlockAI
          body={body}
          type={type}
          showFormButton={showFormButton}
          isLast={isLast}
          sourcingProgress={sourcingProgress}
          isCancelling={isCancelling}
          onCancelSourcing={onCancelSourcing}
        />
      ) : (
        <MessageBlockUser body={body} type={type} name={userName} />
      );
    case TYPES_OF_SENDER.SYSTEM:
      return body.payload.content === "connected" ? (
        <div className="flex gap-2 items-center mx-auto w-fit text-xs text-gray-500">
          <IoIosCheckmarkCircle className="fill-green-600" /> Connected
        </div>
      ) : body.payload.content === "disconnected" ? (
        <div className="flex gap-2 items-center mx-auto w-fit text-xs text-gray-500">
          <IoIosCloseCircle className="fill-red-600" /> Disconnected
        </div>
      ) : (
        <MessageBlockAI body={body} type={type} />
      );
    case TYPES_OF_SENDER.ERROR:
      return <Tag variant={"danger"} text={body.payload.content} />;
    default:
      return;
  }
};

export default MessageBlocks;
